/**
 * ResumeDraftScreen — écran intercalaire si un brouillon est trouvé en
 * sessionStorage à l'arrivée sur /projects/new.
 *
 * Choix UX (D-A4) : pas un toast en bas — un écran qui force le choix.
 * Pourquoi : un toast est trop facilement raté (notamment sur mobile),
 * et l'utilisateur risque d'écraser son brouillon par mégarde en
 * commençant à taper. L'écran l'oblige à dire "reprendre" ou "recommencer"
 * en toute conscience.
 */

import { useTranslation } from "react-i18next";

import type { IntakeMvpDraft } from "../../../lib/intakeDraftStorage";

type Props = {
  draft: IntakeMvpDraft;
  onResume: () => void;
  onDiscard: () => void;
};

export function ResumeDraftScreen({ draft, onResume, onDiscard }: Props) {
  const { t } = useTranslation();
  const updated = new Date(draft.updatedAt);
  const formatted = updated.toLocaleString();

  // Compte des champs renseignés (sans afficher leurs valeurs — privacy).
  const data = draft.data;
  const filled = [
    data.company_id && t("projects.fields.company"),
    data.contact_id && t("projects.fields.contact"),
    data.broker_id && t("projects.fields.broker"),
    data.title?.trim() && t("projects.fields.title"),
    (data.insurer_codes?.length ?? 0) > 0 && t("projects.fields.insurer_codes"),
    (data.guarantee_codes?.length ?? 0) > 0 && t("projects.fields.guarantee_codes"),
  ].filter(Boolean) as string[];

  return (
    <div className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
        {t("projects.intake.resume.title")}
      </h2>
      <p className="mt-2 text-sm text-slate-600">
        {t("projects.intake.resume.description", { date: formatted, step: draft.step })}
      </p>

      {filled.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
          {filled.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onDiscard}
          className="focus-visible:ring-brand-500 touch-manipulation rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("projects.intake.resume.discard")}
        </button>
        <button
          type="button"
          onClick={onResume}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("projects.intake.resume.resume")}
        </button>
      </div>
    </div>
  );
}
